/** Fonction de traduction (clé + paramètres) — `TranslateService.instant` en production, un faux dans les tests. */
export type Translator = (key: string, params?: Record<string, unknown>) => string;

/** « il y a 5 min », « il y a 2 h », « il y a 3 j », puis la date courte au-delà d'une semaine (dans la langue du site). */
export function timeAgo(iso: string, t: Translator, locale: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (Number.isNaN(seconds)) return '';
  if (seconds < 45) return t('NOTIF.AGO.NOW');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('NOTIF.AGO.MIN', { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('NOTIF.AGO.HOUR', { n: hours });
  const days = Math.round(hours / 24);
  if (days < 7) return t('NOTIF.AGO.DAY', { n: days });
  return then.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Chemin interne (ex. « /fr/lyric/1#comment-2 ») d'une URL d'action quand elle pointe vers le site lui-même (même
 * origine, ou chemin relatif) ; null pour une URL externe ou vide. Une URL externe n'est jamais suivie par le routeur.
 */
export function toInternalPath(actionUrl: string | null | undefined, currentOrigin: string): string | null {
  if (!actionUrl) return null;
  try {
    const url = new URL(actionUrl, currentOrigin);
    return url.origin === currentOrigin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}

/** Famille de libellé (clé `NOTIF.TYPE.<FAMILLE>`) de chaque type de notification connu. */
const TYPE_FAMILY: Record<string, 'LIKE' | 'COMMENT' | 'SUBMISSION'> = {
  'lyric.liked': 'LIKE',
  'comment.reply': 'COMMENT',
  'comment.new': 'COMMENT',
  'comment.liked': 'LIKE',
  'submission.approved': 'SUBMISSION',
  'submission.rejected': 'SUBMISSION',
  // Types déjà créés par EngagementService pour les commentaires
  comment_new: 'COMMENT',
  comment_reply: 'COMMENT',
  comment_like: 'COMMENT',
  comment_likes: 'COMMENT',
  comment_edited: 'COMMENT',
};

/** Libellé court du type d'une notification ; le type brut s'il est inconnu. */
export function notificationTypeLabel(type: string, t: Translator): string {
  const family = TYPE_FAMILY[type];
  return family ? t(`NOTIF.TYPE.${family}`) : type;
}

/** Remplace le préfixe de langue d'un chemin du site (« /fr/lyric/1 » → « /en/lyric/1 ») : l'URL est écrite en français côté serveur. */
export function withLang(path: string, lang: string): string {
  return path.replace(/^\/(fr|ht|en|es)(?=\/|$)/, `/${lang}`);
}

/**
 * Titre et message d'une notification dans la langue du site. EngagementService les rédige en français
 * (`UserNotificationRequests`) : on reconnaît ces gabarits d'après le type et le texte, et on en extrait le nombre de
 * personnes et le titre cité. Un texte qui ne correspond à aucun gabarit connu est affiché tel quel.
 */
export function localizeNotification(
  n: { type: string; title: string; message: string },
  t: Translator,
): { title: string; message: string } {
  const count = Number(/^(\d+)\s/.exec(n.title)?.[1] ?? 1);
  const quoted = /«\s*(.+?)\s*»/.exec(n.message)?.[1];
  const plural = count > 1 ? 'MANY' : 'ONE';
  const known = (re: RegExp): boolean => re.test(n.title);

  switch (n.type) {
    case 'lyric.liked':
      if (!known(/^(Quelqu'un aime|\d+ personnes aiment) votre chant$/)) break;
      return {
        title: t(`NOTIF.TPL.LYRIC_LIKED.TITLE_${plural}`, { count }),
        message: t(`NOTIF.TPL.LYRIC_LIKED.MSG_${plural}`, { count, quoted: quoted ? `« ${quoted} »` : t('NOTIF.TPL.YOUR_SONG') }),
      };
    case 'comment.liked':
    case 'comment_likes':
      if (!known(/^(Quelqu'un aime|\d+ personnes aiment) votre commentaire$/)) break;
      return { title: t(`NOTIF.TPL.COMMENT_LIKED.TITLE_${plural}`, { count }), message: t(`NOTIF.TPL.COMMENT_LIKED.MSG_${plural}`, { count }) };
    case 'comment.reply':
    case 'comment_reply':
      if (!known(/^(Réponse|\d+ réponses) à votre commentaire$/)) break;
      return { title: t(`NOTIF.TPL.COMMENT_REPLY.TITLE_${plural}`, { count }), message: t(`NOTIF.TPL.COMMENT_REPLY.MSG_${plural}`, { count }) };
    case 'comment.new':
    case 'comment_new':
      if (!known(/^(Nouveau commentaire|\d+ commentaires sur votre chant)$/)) break;
      return { title: t(`NOTIF.TPL.COMMENT_NEW.TITLE_${plural}`, { count }), message: t(`NOTIF.TPL.COMMENT_NEW.MSG_${plural}`, { count }) };
    case 'submission.approved':
      if (!known(/^Votre soumission a été approuvée$/)) break;
      return {
        title: t('NOTIF.TPL.SUBMISSION_APPROVED.TITLE'),
        message: quoted ? t('NOTIF.TPL.SUBMISSION_APPROVED.MSG_TITLED', { title: quoted }) : t('NOTIF.TPL.SUBMISSION_APPROVED.MSG'),
      };
    case 'submission.rejected': {
      if (!known(/^Votre soumission n'a pas été retenue$/)) break;
      const reason = /^Motif : (.+)$/.exec(n.message)?.[1];
      return {
        title: t('NOTIF.TPL.SUBMISSION_REJECTED.TITLE'),
        message: reason
          ? t('NOTIF.TPL.SUBMISSION_REJECTED.MSG_REASON', { reason })
          : quoted
            ? t('NOTIF.TPL.SUBMISSION_REJECTED.MSG_TITLED', { title: quoted })
            : t('NOTIF.TPL.SUBMISSION_REJECTED.MSG'),
      };
    }
  }
  return { title: n.title, message: n.message };
}
